import { HttpClientModule, HTTP_INTERCEPTORS } from "@angular/common/http";
import { ErrorHandler, NgModule } from "@angular/core";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import {
  MatCheckboxModule,
  MatInputModule,
  MatSortModule,
  MatTableModule,
} from "@angular/material";
import { BrowserModule } from "@angular/platform-browser";
import { NoopAnimationsModule } from "@angular/platform-browser/animations";
import { NgxChartsModule } from "@swimlane/ngx-charts";
import { ngfModule } from "angular-file";
import { SortablejsModule } from "angular-sortablejs";
import {
  MzButtonModule,
  MzCardModule,
  MzCheckboxModule,
  MzChipModule,
  MzCollapsibleModule,
  MzCollectionModule,
  MzDatepickerModule,
  MzDropdownModule,
  MzIconModule,
  MzInjectionService,
  MzInputModule,
  MzModalModule,
  MzModalService,
  MzProgressModule,
  MzRadioButtonModule,
  MzSelectModule,
  MzSpinnerModule,
  MzSwitchModule,
  MzTextareaModule,
  MzToastModule,
  MzTooltipModule,
  MzValidationModule,
} from "ng2-materialize";
import { ClipboardModule } from "ngx-clipboard";
import { CookieService } from "ngx-cookie-service";
import { NgxEditorModule } from "ngx-editor";
import { NgxQRCodeModule } from "ngx-qrcode3";
import { AppComponent } from "./app.component";
import { AppRoutingModule } from "./app.routing";
import { AddStakeholderDialogComponent } from "./components/add-stakeholder-dialog/add-stakeholder-dialog.component";
import { AdminToolsComponent } from "./components/admin-tools/admin-tools.component";
import { AgirErrorDialogComponent } from "./components/agir-error-dialog/agir-error-dialog.component";
import { AgirErrorHandler } from "./components/agir-error-handler/agir-error-handler.component";
import { AnnotationCanvasAnnotationComponent } from "./components/annotation-canvas-annotation/annotation-canvas-annotation.component";
import { AnnotationCanvasComponent } from "./components/annotation-canvas/annotation-canvas.component";
import { AnnotationCarouselComponent } from "./components/annotation-carousel/annotation-carousel.component";
import { AnnotationDetailComponent } from "./components/annotation-detail/annotation-detail.component";
import { AnnotationsComponent } from "./components/annotations/annotations.component";
import { AnnotationtypeDisplayComponent } from "./components/annotationtype-display/annotationtype-display.component";
import { ClickAndEditComponent } from "./components/click-and-edit/click-and-edit.component";
import { ConfirmEpicSwitchDialogComponent } from "./components/confirm-epic-switch-dialog/confirm-epic-switch-dialog.component";
import { ConfirmLeaveBacklogUnsyncedComponent } from "./components/confirm-leave-backlog-unsynced/confirm-leave-backlog-unsynced.component";
import { ConfirmStopVotingComponent } from "./components/confirm-stop-voting/confirm-stop-voting.component";
import { ConfirmSyncComponent } from "./components/confirm-sync/confirm-sync.component";
import { CookiePopupComponent } from "./components/cookie-popup/cookie-popup.component";
import { CreateQuestionnaireDialogComponent } from "./components/create-questionnaire-dialog/create-questionnaire-dialog.component";
import { DeleteItemConfirmationDialogComponent } from "./components/delete-item-confirmation-dialog/delete-item-confirmation-dialog.component";
import { EditAnnotationDialogComponent } from "./components/edit-annotation-dialog/edit-annotation-dialog.component";
import { EditEpicDetailDialogComponent } from "./components/edit-epic-detail-dialog/edit-epic-detail-dialog.component";
import { EditItemDialogComponent } from "./components/edit-item-dialog/edit-item-dialog.component";
import { EditRiskIndicatorDialogComponent } from "./components/edit-risk-indicator-dialog/edit-risk-indicator-dialog.component";
import { EditSprintDialogComponent } from "./components/edit-sprint-dialog/edit-sprint-dialog.component";
import { EditThemeDialogComponent } from "./components/edit-theme-dialog/edit-theme-dialog.component";
import { EpicCardComponent } from "./components/epic-card/epic-card.component";
import { EpicGalleryDialogComponent } from "./components/epic-gallery/epic-gallery.component";
import { EpicSelectorComponent } from "./components/epic-selector/epic-selector.component";
import { EpicsConfirmDeactivateGuard } from "./components/epics/confirm-deactivate.guard";
import { EpicsComponent } from "./components/epics/epics.component";
import { FeatureCanvasItemComponent } from "./components/feature-canvas-item/feature-canvas-item.component";
import { FeatureCanvasConfirmDeactivateGuard } from "./components/feature-canvas/confirm-deactivate.guard";
import { FeatureCanvasComponent } from "./components/feature-canvas/feature-canvas.component";
import { FeedbackModalComponent } from "./components/feedback-modal/feedback-modal.component";
import { FooterComponent } from "./components/footer/footer.component";
import { ForgotPasswordComponent } from "./components/forgot-password/forgot-password.component";
import { HeaderQuestionnaireComponent } from "./components/header-questionnaire/header-questionnaire.component";
import { HeaderComponent } from "./components/header/header.component";
import { ImpressumComponent } from "./components/impressum/impressum.component";
import { IrisBoxPlotElementComponent } from "./components/iris-charts-box-plot-chart/iris-chart-box-plot-element.component";
import { IrisBoxPlotSeriesComponent } from "./components/iris-charts-box-plot-chart/iris-chart-box-plot-series.component";
import { IrisBoxPlotChartComponent } from "./components/iris-charts-box-plot-chart/iris-charts-box-plot-chart.component";
import { IrisCircleSeriesComponent } from "./components/iris-charts-line-chart/iris-charts-circle-series.component";
import { IrisLineChartComponent } from "./components/iris-charts-line-chart/iris-charts-line-chart.component";
import { ComboChartComponent } from "./components/iris-combo-chart/iris-combo-chart.component";
import { ComboSeriesVerticalComponent } from "./components/iris-combo-chart/iris-combo-series-vertical.component";
import { ItemCarouselComponent } from "./components/item-carousel/item-carousel.component";
import { JiraImportComponent } from "./components/jira-import/jira-import.component";
import { JiraSyncProgressComponent } from "./components/jira-sync-progress/jira-sync-progress.component";
import { LicenseExpiredDialogComponent } from "./components/license-expired-dialog/license-expired-dialog.component";
import { LoadingSpinnerComponent } from "./components/loading-spinner/loading-spinner.component";
import { LoginComponent } from "./components/login/login.component";
import { ModalSprintStartComponent } from "./components/modal-sprint-start/modal-sprint-start.component";
import { NavigationQuestionnaireComponent } from "./components/navigation-questionnaire/navigation-questionnaire.component";
import { NavigationComponent } from "./components/navigation/navigation.component";
import { NewPasswordComponent } from "./components/new-password/new-password.component";
import { NgxGalleryActionComponent } from "./components/ngx-gallery/ngx-gallery-action.component";
import { NgxGalleryArrowsComponent } from "./components/ngx-gallery/ngx-gallery-arrows.component";
import { NgxGalleryBulletsComponent } from "./components/ngx-gallery/ngx-gallery-bullets.component";
import { NgxGalleryHelperService } from "./components/ngx-gallery/ngx-gallery-helper.service";
import { NgxGalleryImageComponent } from "./components/ngx-gallery/ngx-gallery-image.component";
import { NgxGalleryPreviewComponent } from "./components/ngx-gallery/ngx-gallery-preview.component";
import { NgxGalleryThumbnailsComponent } from "./components/ngx-gallery/ngx-gallery-thumbnails.component";
import { NgxGalleryComponent } from "./components/ngx-gallery/ngx-gallery.component";
import { NotFound404Component } from "./components/not-found-404/not-found-404.component";
import { PlanningPokerAnnotationHeaderComponent } from "./components/planning-poker-annotation-header/planning-poker-annotation-header.component";
import { PlanningPokerFeatureHeaderComponent } from "./components/planning-poker-feature-header/planning-poker-feature-header.component";
import { PlanningPokerHomeComponent } from "./components/planning-poker-home/planning-poker-home.component";
import { PlanningPokerRiskIndicatorHeaderComponent } from "./components/planning-poker-risk-indicator-header/planning-poker-risk-indicator-header.component";
import { PlanningPokerToastsComponent } from "./components/planning-poker-toasts/planning-poker-toasts.component";
import { PlayingCardComponent } from "./components/playing-card/playing-card.component";
import { PrivacyNoticeComponent } from "./components/privacy-notice/privacy-notice.component";
import { ProjectDashboardComponent } from "./components/project-dashboard/project-dashboard.component";
import { ProjectOverviewAdminComponent } from "./components/project-overview-admin/project-overview-admin.component";
import { ProjectsComponent } from "./components/projects/projects.component";
import { QrCodeComponent } from "./components/qrcode/qrcode.component";
import { QuestionnairePasswordDialogComponent } from "./components/questionnaire-password-dialog/questionnaire-password-dialog.component";
import { QuestionnaireQuestionsComponent } from "./components/questionnaire-questions/questionnaire-questions.component";
import { QuestionnaireComponent } from "./components/questionnaire/questionnaire.component";
import { QuestionnaireItemComponent } from "./components/questionnaires-item/questionnaires-item.component";
import { QuestionnairesComponent } from "./components/questionnaires/questionnaires.component";
import { RegistrationComponent } from "./components/registration/registration.component";
import { ResendActivationlinkComponent } from "./components/resend-activationlink/resend-activationlink.component";
import { RetrospectiveWizardStartComponent } from "./components/retrospective-wizard-1-start/retrospective-wizard-1-start.component";
import { RetrospectiveWizardInviteComponent } from "./components/retrospective-wizard-2-invite/retrospective-wizard-2-invite.component";
import { RetrospectiveWizardVoteComponent } from "./components/retrospective-wizard-3-vote/retrospective-wizard-3-vote.component";
import { RetrospectiveWizardResultsComponent } from "./components/retrospective-wizard-4-results/retrospective-wizard-4-results.component";
import { RetrospectiveWizardComponent } from "./components/retrospective-wizard/retrospective-wizard.component";
import { RiskIndicatorCardComponent } from "./components/risk-indicator-card/risk-indicator-card.component";
import { IndicatorNavigationComponent } from "./components/risk-indicator/indicator-navigation/indicator-navigation.component";
import { RiskIndicatorComponent } from "./components/risk-indicator/risk-indicator.component";
import { RiskIndicatorsDeactivateGuard } from "./components/risk-indicators/risk-indicators-deactivate.guard";
import { RiskIndicatorsComponent } from "./components/risk-indicators/risk-indicators.component";
import { SendQuestionnaireComponent } from "./components/send-questionnaire/send-questionnaire.component";
import { SprintCardComponent } from "./components/sprint-card/sprint-card.component";
import { SprintsConfirmDeactivateGuard } from "./components/sprints/confirm-deactivate.guard";
import { SprintsComponent } from "./components/sprints/sprints.component";
import { StakeholderHeaderComponent } from "./components/stakeholder-header/stakeholder-header.component";
import { SubscriptionComponent } from "./components/subscription/subscription.component";
import { SubscriptionAddressComponent } from "./components/subscription/subscription-address/subscription-address.component";
import { SubscriptionLicenseComponent } from "./components/subscription/subscription-license/subscription-license.component";
import { TermsofserviceComponent } from "./components/termsofservice/termsofservice.component";
import { UserBehaviorComponent } from "./components/user-behavior/user-behavior.component";
import { UserDetailComponent } from "./components/user-detail/user-detail.component";
import { UserProfileComponent } from "./components/user-profile/user-profile.component";
import { VotingRiskIndicatorComponent } from "./components/voting-risk-indicator/voting-risk-indicator.component";
import { VotingComponent } from "./components/voting/voting.component";
import { AutofocusDirective } from "./directives/autofocus.directive";
import { EqualValidatorDirective } from "./directives/equal-validator.directive";
import { LowercaseDirective } from "./directives/lowercase.directive";
import { GetImagePipe } from "./pipes/getimage.pipe";
import { AccessGuardService } from "./services/access-guard/access-guard.service";
import { AdminToolsService } from "./services/admin-tools/admin-tools.service";
import { AnnotationService } from "./services/annotation/annotation.service";
import { AnnotationtypeService } from "./services/annotationtype/annotationtype.service";
import { AnnotationvariantService } from "./services/annotationvariant/annotationvariant.service";
import { AuthService } from "./services/auth/auth.service";
import { TokenInterceptor } from "./services/auth/token.interceptor";
import { EpicService } from "./services/epic/epic.service";
import { ExportService } from "./services/export/export.service";
import { FeatureService } from "./services/feature/feature.service";
import { FeedbackService } from "./services/feedback/feedback.service";
import { JiraService } from "./services/jira/jira.service";
import { LoggedInGuardService } from "./services/logged-in-guard/logged-in-guard.service";
import { NoCacheInterceptor } from "./services/no-cache-interceptor/no-cache.interceptor";
import { NotProductionService } from "./services/not-production/not-production.service";
import { PlanningPokerService } from "./services/planning-poker/planning-poker.service";
import { ProjectService } from "./services/project/project.service";
import { QuestionnaireDeactivateGuard } from "./services/questionnaire-deactivate/questionnaire-deactivate.guard";
import { QuestionnaireService } from "./services/questionnaire/questionnaire.service";
import { RegistrationService } from "./services/registration/registration.service";
import { RetrospectiveService } from "./services/retrospective/retrospective.service";
import { RiskIndicatorService } from "./services/risk-indicator/risk-indicator.service";
import { RouteDataService } from "./services/route-data/route-data.service";
import { SessionService } from "./services/session/session.service";
import { SprintService } from "./services/sprint/sprint.service";
import { SubscriptionService } from "./services/subscription/subscription.service";
import { SyncProgressService } from "./services/sync-progress/sync-progress.service";
import { UserBehaviorService } from "./services/user-behavior/user-behavior.service";
import { UserService } from "./services/user/user.service";
import { DatePipe } from "./pipes/date.pipe";
import { VideoComponent } from "./components/video/video.component";
import { HelpComponent } from "./components/help/help.component";

@NgModule({
  declarations: [
    AppComponent,
    LoginComponent,
    ProjectsComponent,
    JiraImportComponent,
    ProjectDashboardComponent,
    HeaderComponent,
    NavigationComponent,
    RegistrationComponent,
    AutofocusDirective,
    GetImagePipe,
    LowercaseDirective,
    EqualValidatorDirective,
    ForgotPasswordComponent,
    NewPasswordComponent,
    UserProfileComponent,
    FeatureCanvasComponent,
    EpicSelectorComponent,
    ItemCarouselComponent,
    AgirErrorDialogComponent,
    LicenseExpiredDialogComponent,
    PlanningPokerHomeComponent,
    PlanningPokerFeatureHeaderComponent,
    PlanningPokerAnnotationHeaderComponent,
    PlanningPokerToastsComponent,
    QrCodeComponent,
    QrCodeComponent,
    ClickAndEditComponent,
    NotFound404Component,
    LoadingSpinnerComponent,
    ConfirmSyncComponent,
    ConfirmLeaveBacklogUnsyncedComponent,
    AnnotationsComponent,
    AnnotationDetailComponent,
    ResendActivationlinkComponent,
    FeatureCanvasItemComponent,
    DeleteItemConfirmationDialogComponent,
    EditItemDialogComponent,
    EditThemeDialogComponent,
    StakeholderHeaderComponent,
    AnnotationCanvasComponent,
    AnnotationCanvasAnnotationComponent,
    AnnotationCarouselComponent,
    EditAnnotationDialogComponent,
    AnnotationtypeDisplayComponent,
    VotingComponent,
    TermsofserviceComponent,
    PlayingCardComponent,
    PrivacyNoticeComponent,
    ImpressumComponent,
    FooterComponent,
    EditSprintDialogComponent,
    SprintsComponent,
    SprintCardComponent,
    IrisLineChartComponent,
    IrisCircleSeriesComponent,
    JiraSyncProgressComponent,
    RiskIndicatorsComponent,
    RiskIndicatorCardComponent,
    EditRiskIndicatorDialogComponent,
    RiskIndicatorComponent,
    RetrospectiveWizardComponent,
    RetrospectiveWizardStartComponent,
    RetrospectiveWizardInviteComponent,
    RetrospectiveWizardVoteComponent,
    RetrospectiveWizardResultsComponent,
    VotingRiskIndicatorComponent,
    PlanningPokerRiskIndicatorHeaderComponent,
    ConfirmStopVotingComponent,
    ModalSprintStartComponent,
    IrisBoxPlotChartComponent,
    IrisBoxPlotSeriesComponent,
    EpicsComponent,
    EpicCardComponent,
    IrisBoxPlotElementComponent,
    EditEpicDetailDialogComponent,
    UserBehaviorComponent,
    FeedbackModalComponent,
    ConfirmEpicSwitchDialogComponent,
    EpicGalleryDialogComponent,
    NgxGalleryActionComponent,
    NgxGalleryArrowsComponent,
    NgxGalleryBulletsComponent,
    NgxGalleryImageComponent,
    NgxGalleryThumbnailsComponent,
    NgxGalleryPreviewComponent,
    NgxGalleryComponent,
    CookiePopupComponent,
    HeaderQuestionnaireComponent,
    IndicatorNavigationComponent,
    NavigationQuestionnaireComponent,
    QuestionnairesComponent,
    QuestionnaireItemComponent,
    QuestionnaireComponent,
    QuestionnaireQuestionsComponent,
    CreateQuestionnaireDialogComponent,
    AddStakeholderDialogComponent,
    QuestionnairePasswordDialogComponent,
    ComboChartComponent,
    ComboSeriesVerticalComponent,
    SubscriptionComponent,
    SubscriptionAddressComponent,
    SubscriptionLicenseComponent,
    UserDetailComponent,
    ProjectOverviewAdminComponent,
    SendQuestionnaireComponent,
    AdminToolsComponent,
    DatePipe,
    VideoComponent,
    HelpComponent,
  ],
  entryComponents: [
    JiraImportComponent,
    AgirErrorDialogComponent,
    LicenseExpiredDialogComponent,
    ConfirmSyncComponent,
    ConfirmLeaveBacklogUnsyncedComponent,
    ConfirmStopVotingComponent,
    ModalSprintStartComponent,
    FeedbackModalComponent,
    ConfirmEpicSwitchDialogComponent,
  ],
  imports: [
    BrowserModule,
    AppRoutingModule,
    HttpClientModule,
    FormsModule,
    NoopAnimationsModule,
    MzTooltipModule,
    MzDropdownModule,
    MzButtonModule,
    MzModalModule,
    MzSpinnerModule,
    MzToastModule,
    MzCardModule,
    MzSelectModule,
    MzProgressModule,
    MzValidationModule,
    ReactiveFormsModule,
    MzCollectionModule,
    MzCollapsibleModule,
    MzCheckboxModule,
    MzRadioButtonModule,
    NgxQRCodeModule,
    SortablejsModule.forRoot({}),
    MzSwitchModule,
    ClipboardModule,
    NgxEditorModule,
    MzTextareaModule,
    MzIconModule,
    MzInputModule,
    MzChipModule,
    NgxChartsModule,
    ngfModule,
    MzDatepickerModule,
    MatTableModule,
    MatSortModule,
    MatInputModule,
    MatCheckboxModule,
  ],
  providers: [
    {
      provide: HTTP_INTERCEPTORS,
      useClass: NoCacheInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TokenInterceptor,
      multi: true,
    },
    {
      provide: ErrorHandler,
      useClass: AgirErrorHandler,
    },
    CookieService,
    FeedbackService,
    QuestionnaireService,
    NgxGalleryHelperService,
    RouteDataService,
    FeatureCanvasConfirmDeactivateGuard,
    SprintsConfirmDeactivateGuard,
    EpicsConfirmDeactivateGuard,
    RetrospectiveService,
    SyncProgressService,
    SprintService,
    RiskIndicatorService,
    NotProductionService,
    ExportService,
    RiskIndicatorsDeactivateGuard,
    PlanningPokerService,
    MzInjectionService,
    MzModalService,
    AuthService,
    SessionService,
    ProjectService,
    FeatureService,
    EpicService,
    RegistrationService,
    UserService,
    JiraService,
    LoggedInGuardService,
    AccessGuardService,
    AnnotationService,
    AnnotationtypeService,
    AnnotationvariantService,
    QuestionnaireDeactivateGuard,
    UserBehaviorService,
    AdminToolsService,
    SubscriptionService,
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}



// WEBPACK FOOTER //
// /agir/src/app/app.module.ts